import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Copy, RefreshCw, Archive, ArchiveRestore, UserMinus, ChevronLeft, Wand2, BarChart3, ClipboardList, Clock, AlertCircle, QrCode } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import Seo from "@/components/Seo";
import MissionStudio from "@/components/MissionStudio";
import { useI18n } from "@/context/I18nContext";
import { langDisplayName } from "@/lib/languages";
import { eduError } from "@/lib/eduErrors";
import { ClassAssetList } from "@/pages/AssetAssignment";

const DUE = { overdue: "bg-coral text-white", due_today: "bg-butter", due_soon: "bg-peach", scheduled: "bg-white" };

export default function ClassPage() {
  const { classId } = useParams();
  const { t, locale } = useI18n();
  const [cls, setCls] = useState(null);
  const [progress, setProgress] = useState(null);
  const [err, setErr] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [insights, setInsights] = useState(null);
  const [studio, setStudio] = useState(false);
  const [results, setResults] = useState(null);
  const d = (x) => (x ? new Date(x).toLocaleDateString(locale) : "—");

  const load = async () => {
    try { setCls((await api.get(`/classes/${classId}`)).data); }
    catch { setErr(true); }
  };
  const loadPhase4 = async () => {
    try {
      const [a, i] = await Promise.all([api.get(`/classes/${classId}/assignments`), api.get(`/classes/${classId}/insights`)]);
      setAssignments(a.data.assignments || []);
      setInsights(i.data);
    } catch { /* teacher-only; ignore if not permitted */ }
  };
  useEffect(() => {
    load(); loadPhase4();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId]);

  const fail = (e) => toast.error(eduError(e, t, t("edu.teach.failed")));
  const regen = async (what) => {
    try { await api.post(`/classes/${classId}/regenerate-${what}`); toast.success(t("edu.cls.regenerated")); load(); } catch (e) { fail(e); }
  };
  const toggleArchive = async () => {
    const action = cls.status === "archived" ? "restore" : "archive";
    try { await api.post(`/classes/${classId}/${action}`); load(); } catch (e) { fail(e); }
  };
  const removeStudent = async (uid) => {
    try { await api.post(`/classes/${classId}/members/${uid}/remove`); toast.success(t("edu.cls.removed")); setProgress(null); load(); } catch (e) { fail(e); }
  };
  const viewProgress = async (uid) => {
    try { setProgress({ uid, ...(await api.get(`/classes/${classId}/students/${uid}/progress`)).data }); }
    catch { toast.error(t("edu.cls.progressFailed")); }
  };
  const viewResults = async (aid) => {
    try { setResults((await api.get(`/assignments/${aid}/results`)).data); } catch { toast.error(t("edu.cls.resultsFailed")); }
  };

  if (err) return <AppLayout><div className="max-w-3xl mx-auto px-4 py-8"><p className="text-coral" data-testid="class-no-access">{t("edu.cls.noAccess")}</p></div></AppLayout>;
  if (!cls) return <AppLayout><div className="max-w-3xl mx-auto px-4 py-8"><div className="nb-card h-40 animate-pulse" /></div></AppLayout>;

  const students = (cls.roster || []).filter((r) => r.role === "student" && r.status === "active");
  const expired = !!cls.pilot_expired;
  const archived = cls.status === "archived";

  return (
    <AppLayout>
      <Seo title={`${cls.name} · FluentLoop`} noindex />
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Link to="/teacher" className="text-sm text-gray-600 flex items-center gap-1" data-testid="class-back-link"><ChevronLeft className="w-4 h-4 rtl:rotate-180" /> {t("teacher.dashboard")}</Link>
        <div className="flex items-center justify-between flex-wrap gap-3 mt-2">
          <div>
            <h1 className="font-cabinet font-extrabold text-3xl" dir="auto" data-testid="class-title">{cls.name}</h1>
            <p className="text-gray-600 text-sm">{langDisplayName(cls.target_language, locale)} · {cls.level} · <bdi>{cls.organization?.name}</bdi>{archived ? ` · ${t("edu.teach.archived")}` : ""}</p>
            {cls.description && <p className="text-sm mt-1" dir="auto" data-testid="class-description">{cls.description}</p>}
          </div>
          {!expired && (
            <button onClick={toggleArchive} className="nb-btn-outline py-2 px-3 text-sm" data-testid="archive-toggle">
              {archived ? <><ArchiveRestore className="w-4 h-4" /> {t("edu.cls.restore")}</> : <><Archive className="w-4 h-4" /> {t("edu.cls.archive")}</>}
            </button>
          )}
        </div>

        {expired && <ExpiredBanner t={t} />}
        {!expired && <InvitePanel cls={cls} t={t} onRegen={regen} />}

        <h2 className="font-cabinet font-extrabold text-2xl mt-8">{t("edu.cls.roster", { n: students.length })}</h2>
        <div className="space-y-2 mt-3" data-testid="class-roster">
          {students.length === 0 && <p className="text-gray-500 text-sm" data-testid="roster-empty">{t("edu.cls.noStudents")}</p>}
          {students.map((s) => (
            <div key={s.user_id} className="nb-card p-3 flex items-center justify-between gap-2" data-testid={`roster-${s.user_id}`}>
              <div><div className="font-bold" dir="auto">{s.display_name}</div><div className="text-xs text-gray-500">{t("edu.cls.joined", { date: d(s.joined_at) })}</div></div>
              <div className="flex gap-2">
                <button onClick={() => viewProgress(s.user_id)} className="nb-btn-outline py-1.5 px-3 text-xs" data-testid={`view-progress-${s.user_id}`}>{t("edu.cls.progress")}</button>
                <button onClick={() => removeStudent(s.user_id)} aria-label={t("edu.cls.remove")} title={t("edu.cls.remove")} className="nb-btn-outline py-1.5 px-3 text-xs text-coral border-coral" data-testid={`remove-student-${s.user_id}`}><UserMinus className="w-3 h-3" /></button>
              </div>
            </div>
          ))}
        </div>

        {progress && <ProgressPanel p={progress} t={t} d={d} />}

        <div className="flex items-center justify-between mt-10 gap-2 flex-wrap">
          <h2 className="font-cabinet font-extrabold text-2xl flex items-center gap-2"><ClipboardList className="w-6 h-6" /> {t("edu.cls.assignments")}</h2>
          {!archived && !expired && (
            <button onClick={() => setStudio(true)} className="nb-btn-primary py-2 px-4 text-sm" data-testid="create-mission-btn"><Wand2 className="w-4 h-4" /> {t("edu.cls.createMission")}</button>
          )}
        </div>
        <div className="space-y-2 mt-3" data-testid="assignments-list">
          {assignments.length === 0 && <p className="text-gray-500 text-sm" data-testid="assignments-empty">{t("edu.cls.noAssignments")}</p>}
          {assignments.map((a) => (
            <button key={a.assignment_id} onClick={() => viewResults(a.assignment_id)} className="nb-card p-3 w-full text-start flex items-center justify-between gap-3" data-testid={`assignment-row-${a.assignment_id}`}>
              <div className="min-w-0">
                <div className="font-bold truncate" dir="auto">{a.title}</div>
                <div className="text-xs text-gray-500 flex flex-wrap gap-2 mt-0.5">
                  <span>{a.level}</span>
                  {a.estimated_minutes && <span>· {t("edu.cls.minutes", { n: a.estimated_minutes })}</span>}
                  {a.due_date && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {d(a.due_date)}</span>}
                  {a.due_state && a.due_state !== "scheduled" && <span className={`nb-tag text-[10px] py-0 ${DUE[a.due_state]}`}>{t(`edu.cls.due.${a.due_state}`)}</span>}
                </div>
              </div>
              <div className="text-end shrink-0">
                <div className="font-cabinet font-extrabold text-lg" dir="ltr">{a.stats.completed}/{a.stats.students}</div>
                <div className="text-[10px] text-gray-500 uppercase">{t("edu.cls.completedLower")}</div>
              </div>
            </button>
          ))}
        </div>

        {insights && <Insights ins={insights} t={t} />}
        <ClassAssetList classId={classId} />
      </div>

      {studio && (
        <MissionStudio classId={classId} className={cls.name} context={{ target_language: cls.target_language, level: cls.level }}
          onClose={() => setStudio(false)} onAssigned={loadPhase4} />
      )}
      {results && <ResultsModal results={results} t={t} onClose={() => setResults(null)} />}
    </AppLayout>
  );
}

function ExpiredBanner({ t }) {
  return (
    <div className="nb-card bg-peach p-4 mt-6 flex gap-3" data-testid="pilot-expired-banner">
      <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
      <div><div className="font-bold">{t("edu.cls.expiredH")}</div><p className="text-sm mt-1">{t("edu.cls.expiredB")}</p></div>
    </div>
  );
}

function InvitePanel({ cls, t, onRegen }) {
  const [showQr, setShowQr] = useState(false);
  const inviteUrl = `${window.location.origin}/join/${cls.invite_token}`;
  const copy = (text, msg) => { navigator.clipboard.writeText(text); toast.success(msg); };
  return (
    <div className="nb-card p-5 mt-6" data-testid="invite-panel">
      <h3 className="font-bold">{t("edu.cls.invite")}</h3>
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <span className="nb-tag bg-mint text-ink text-base" data-testid="class-code">{t("edu.cls.classCode")}: <span dir="ltr">{cls.class_code}</span></span>
        <button onClick={() => copy(cls.class_code, t("edu.cls.codeCopied"))} className="nb-btn-outline py-1.5 px-3 text-xs" data-testid="copy-code"><Copy className="w-3 h-3" /> {t("edu.cls.copyCode")}</button>
        <button onClick={() => onRegen("code")} className="nb-btn-outline py-1.5 px-3 text-xs" data-testid="regen-code"><RefreshCw className="w-3 h-3" /> {t("edu.cls.newCode")}</button>
      </div>
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        <button onClick={() => copy(inviteUrl, t("edu.cls.linkCopied"))} className="nb-btn-outline py-1.5 px-3 text-xs" data-testid="copy-invite-link"><Copy className="w-3 h-3" /> {t("edu.cls.copyLink")}</button>
        <button onClick={() => onRegen("invite")} className="nb-btn-outline py-1.5 px-3 text-xs" data-testid="regen-invite"><RefreshCw className="w-3 h-3" /> {t("edu.cls.newLink")}</button>
        <button onClick={() => setShowQr((v) => !v)} className="nb-btn-outline py-1.5 px-3 text-xs" data-testid="toggle-qr"><QrCode className="w-3 h-3" /> {showQr ? t("edu.cls.hideQr") : t("edu.cls.showQr")}</button>
      </div>
      {showQr && (
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <div className="bg-white p-2 border-2 border-ink rounded"><QRCodeSVG value={inviteUrl} size={128} level="M" title={t("edu.cls.qrAlt")} data-testid="class-qr" /></div>
          <p className="text-xs text-gray-600 max-w-xs">{t("edu.cls.qrNote")}</p>
        </div>
      )}
    </div>
  );
}

const Metric = ({ label, v, id }) => (
  <div className="bg-white border-2 border-ink rounded p-2" data-testid={id}><div className="font-cabinet font-extrabold text-xl">{v ?? "—"}</div><div className="text-xs text-gray-500">{label}</div></div>
);

function ProgressPanel({ p, t, d }) {
  return (
    <div className="nb-card bg-sky p-5 mt-4" data-testid="student-progress">
      <h3 className="font-cabinet font-bold text-lg" dir="auto">{p.display_name}</h3>
      <p className="text-xs text-gray-600 mt-1">{t("edu.cls.privacyNote")}</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-sm">
        <Metric label={t("edu.cls.aCompleted")} v={<span dir="ltr">{p.assignments_completed}/{p.assignments_total}</span>} id="prog-completed" />
        <Metric label={t("edu.cls.inProgress")} v={p.assignments_in_progress} id="prog-in-progress" />
        <Metric label={t("edu.cls.notStarted")} v={p.assignments_not_started} id="prog-not-started" />
        <Metric label={t("edu.cls.lastActivity")} v={d(p.last_class_activity)} id="prog-last-activity" />
      </div>
    </div>
  );
}

function Insights({ ins, t }) {
  return (
    <div className="nb-card bg-lavender p-5 mt-8" data-testid="class-insights">
      <h3 className="font-cabinet font-bold text-lg flex items-center gap-2"><BarChart3 className="w-5 h-5" /> {t("edu.cls.insights")}</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
        <Metric label={t("edu.cls.completionRate")} v={`${Math.round((ins.completion_rate || 0) * 100)}%`} />
        <Metric label={t("edu.cls.participation")} v={<span dir="ltr">{ins.participation.active}/{ins.participation.total}</span>} />
        <Metric label={t("edu.cls.assignments")} v={ins.assignments} />
      </div>
      {ins.common_vocabulary?.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-bold uppercase text-gray-600 mb-1">{t("edu.cls.assignedWords")}</div>
          <div className="flex flex-wrap gap-1.5">
            {ins.common_vocabulary.map((v, i) => <span key={`${v.word}-${i}`} className="nb-tag bg-white text-xs normal-case" dir="auto">{v.word} <span className="text-gray-400">×{v.count}</span></span>)}
          </div>
        </div>
      )}
      {ins.areas_needing_practice?.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-bold uppercase text-gray-600 mb-1">{t("edu.cls.needPractice")}</div>
          {ins.areas_needing_practice.map((w, i) => <div key={`${w.title}-${i}`} className="text-sm">• <bdi>{w.title}</bdi> <span className="text-gray-400">({t("edu.cls.pctDone", { n: Math.round(w.completion_rate * 100) })})</span></div>)}
        </div>
      )}
    </div>
  );
}

function ResultsModal({ results, t, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] bg-ink/60 flex items-start justify-center overflow-auto p-4" onClick={onClose} data-testid="results-modal">
      <div className="bg-paper border-2 border-ink rounded-2xl shadow-hard w-full max-w-lg my-6" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b-2 border-ink flex items-center justify-between gap-2">
          <h3 className="font-cabinet font-bold" dir="auto">{results.assignment.title}</h3>
          <button onClick={onClose} className="text-sm underline" data-testid="results-close">{t("edu.cls.close")}</button>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-3 gap-2 mb-4">
            <Metric label={t("edu.cls.completed")} v={results.stats.completed} />
            <Metric label={t("edu.cls.inProgress")} v={results.stats.in_progress} />
            <Metric label={t("edu.cls.notStarted")} v={results.stats.not_started} />
          </div>
          <div className="space-y-2 max-h-80 overflow-auto no-scrollbar">
            {results.students.map((s) => (
              <div key={s.user_id} className="flex items-center justify-between border-2 border-ink rounded-lg px-3 py-2 text-sm">
                <span className="font-bold" dir="auto">{s.display_name}</span>
                <StatusTag s={s} t={t} />
              </div>
            ))}
            {results.students.length === 0 && <p className="text-sm text-gray-500">{t("edu.cls.noStudentsInClass")}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

const STATUS = { completed: ["completed", "bg-mint"], in_progress: ["inProgress", "bg-butter"], not_started: ["notStarted", "bg-white"] };
function StatusTag({ s, t }) {
  const [k, c] = STATUS[s.status] || STATUS.not_started;
  const late = s.status === "completed" && s.completed_after_due;
  return <span className={`nb-tag text-xs ${c}`}>{t(`edu.cls.${k}`)}{late ? ` ${t("edu.cls.late")}` : ""}{s.status === "in_progress" && s.total_steps ? ` ${s.completed_steps}/${s.total_steps}` : ""}</span>;
}
